import { AiChatMessage } from "../../types/Ai";

/** The CRX records a conversation can be about (GET ai/chats?linked=kind:id). */
export type AskLinkKind = "opportunity" | "client" | "provider" | "company" | "lead";

export interface AskChatLink {
    kind: AskLinkKind;
    id: number;
    name: string;
}

/** One row of GET ai/chats. */
export interface AskChatSummary {
    chatId: number;
    title: string | null;
    updatedAt: string;
    createdAt?: string;
    hasFiles: boolean;
    linked: AskChatLink | null;
    importedResearchId: number | null;
}

/** A file, pasted text or web page read in the conversation (GET ai/chats/:id). */
export interface AskChatMaterial {
    messageId: number;
    name: string;
    kind: string;
    chars: number;
    parts: string;
    truncated: boolean;
    publicFigures: boolean;
    sourceUrl: string | null;
}

/** GET ai/chats/:id. */
export interface AskChatDetail {
    chatId: number;
    title: string | null;
    linked: AskChatLink | null;
    hasFiles: boolean;
    importedResearchId: number | null;
    materials: AskChatMaterial[];
    messages: AiChatMessage[];
}

export const chatTitle = (chat: { title: string | null }): string => chat.title?.trim() || "New conversation";

export type AskChatGroupLabel = "Today" | "This week" | "Earlier";

/** The rail's groups by last activity, newest first; empty groups are left out. */
export const groupChats = (chats: AskChatSummary[], now = new Date()): Array<{ label: AskChatGroupLabel; chats: AskChatSummary[] }> => {
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const weekAgo = startOfToday - 6 * 24 * 60 * 60 * 1000;
    const groups: Record<AskChatGroupLabel, AskChatSummary[]> = { Today: [], "This week": [], Earlier: [] };
    [...chats].sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()).forEach((chat) => {
        const at = new Date(chat.updatedAt).getTime();
        groups[at >= startOfToday ? "Today" : at >= weekAgo ? "This week" : "Earlier"].push(chat);
    });
    return (["Today", "This week", "Earlier"] as AskChatGroupLabel[]).filter((label) => groups[label].length).map((label) => ({ label, chats: groups[label] }));
};

/** `?link=kind:id&name=…` on /ask: the record a new conversation is about. */
export const parseLinkParam = (search: URLSearchParams): AskChatLink | null => {
    const [kind, id] = (search.get("link") ?? "").split(":");
    if (!kind || !/^\d+$/.test(id ?? "")) return null;
    return { kind: kind as AskLinkKind, id: Number(id), name: search.get("name") ?? "" };
};
